import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export default function initKaraoke() {
  const elementsToSplit = document.querySelectorAll(".split-lines")

  // Check if the letter is a single Czech letter
  function isCzechSingleLetter(letter) {
    const czechSingleLetters = ["a", "i", "o", "u", "k", "s", "v", "z"]
    return czechSingleLetters.includes(letter.toLowerCase())
  }

  // Function to handle Czech single letters
  function handleCzechSingleLetters(element) {
    const lines = element.querySelectorAll(".line")
    lines.forEach((line, lineIndex) => {
      const words = line.querySelectorAll(".word")
      words.forEach((word, wordIndex) => {
        const text = word.textContent.trim()
        if (text.length === 1 && isCzechSingleLetter(text)) {
          if (wordIndex < words.length - 1) {
            const nextWord = words[wordIndex + 1]
            // Glue with NBSP and remove any accidental leading space
            nextWord.textContent = `${text}\u00A0${nextWord.textContent.trimStart()}`
            word.remove()
          } else if (lineIndex < lines.length - 1) {
            const nextLine = lines[lineIndex + 1]
            const nextLineWords = nextLine.querySelectorAll(".word")
            if (nextLineWords.length > 0) {
              const firstWordInNextLine = nextLineWords[0]
              // Glue across lines with NBSP and clean leading space
              firstWordInNextLine.textContent = `${text}\u00A0${firstWordInNextLine.textContent.trimStart()}`
              word.remove()
            }
          }
        }
      })
    })
  }

  // Manual line breaking function that works in all browsers
  function createLines(element) {
    // Always base splitting on the original untouched text to avoid losing spaces
    const originalText = element.dataset.karaokeOriginal ?? element.textContent
    const words = originalText.split(/\s+/).filter((word) => word.length > 0)
    const containerWidth = element.offsetWidth

    // Create a test element to measure text width
    const testElement = document.createElement("span")
    testElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: ${getComputedStyle(element).fontFamily};
      font-size: ${getComputedStyle(element).fontSize};
      font-weight: ${getComputedStyle(element).fontWeight};
      letter-spacing: ${getComputedStyle(element).letterSpacing};
      word-spacing: ${getComputedStyle(element).wordSpacing};
      line-height: ${getComputedStyle(element).lineHeight};
    `
    document.body.appendChild(testElement)

    const lines = []
    let currentLine = []

    for (let i = 0; i < words.length; i++) {
      const testWords = [...currentLine, words[i]]
      testElement.textContent = testWords.join(" ")

      // Force layout recalculation for Safari
      testElement.offsetWidth

      if (testElement.offsetWidth > containerWidth && currentLine.length > 0) {
        // Current line is full, start a new line
        lines.push(currentLine)
        currentLine = [words[i]]
      } else {
        currentLine = testWords
      }
    }

    // Add the last line
    if (currentLine.length > 0) {
      lines.push(currentLine)
    }

    document.body.removeChild(testElement)
    return lines
  }

  // Create line and word elements manually
  function createLineElements(element) {
    const lineArrays = createLines(element)

    // Clear the element
    element.innerHTML = ""

    lineArrays.forEach((lineWords, lineIndex) => {
      const lineDiv = document.createElement("div")
      lineDiv.className = "line"
      lineDiv.style.cssText = `
        position: relative;
        display: block;
        overflow: hidden;
        width: 100%;
      `

      lineWords.forEach((wordText, wordIndex) => {
        const wordSpan = document.createElement("span")
        wordSpan.className = "word"
        wordSpan.textContent = wordText
        wordSpan.style.display = "inline-block"
        lineDiv.appendChild(wordSpan)

        // Add space after each word except the last
        if (wordIndex < lineWords.length - 1) {
          lineDiv.appendChild(document.createTextNode(" "))
        }
      })

      // Add line mask
      const lineMask = document.createElement("div")
      lineMask.className = "line-mask"
      lineDiv.appendChild(lineMask)

      element.appendChild(lineDiv)
    })
  }

  // Split the text up
  function runSplit() {
    elementsToSplit.forEach((element, index) => {
      // Cache original text once to keep a stable source across resizes/content changes
      if (!element.dataset.karaokeOriginal) {
        // Use textContent to preserve spacing and avoid HTML entities
        element.dataset.karaokeOriginal = element.textContent
      }

      // Use our manual line creation instead of GSAP SplitText
      createLineElements(element)

      // Handle Czech single letters
      handleCzechSingleLetters(element)
    })

    checkTextAlignment()
  }

  // Check and apply text alignment
  function checkTextAlignment() {
    elementsToSplit.forEach((element) => {
      const computedStyle = window.getComputedStyle(element)
      const textAlign = computedStyle.textAlign
      element.querySelectorAll(".line").forEach((line) => {
        line.style.textAlign = textAlign
      })
    })
  }

  // Initialize animations
  function createAnimation() {
    // Kill existing ScrollTriggers
    ScrollTrigger.getAll().forEach((trigger) => {
      // Remove any triggers created for our split-lines blocks
      if (trigger.vars.trigger?.classList?.contains("split-lines")) {
        trigger.kill()
      }
    })

    // Process each text block separately
    elementsToSplit.forEach((textBlock) => {
      const lines = textBlock.querySelectorAll(".line")
      const lineHeight = window.innerWidth < 1024 ? 80 : 120 // 80 for small screens, 120 for larger

      lines.forEach((line, lineIndex) => {
        const lineMask = line.querySelector(".line-mask")
        if (lineMask) {
          // Set initial state
          gsap.set(lineMask, { width: "100%" })

          // Calculate scroll positions for sequential reveal
          const blockTop = textBlock.offsetTop
          const startOffset = blockTop + lineIndex * lineHeight
          const endOffset = startOffset + lineHeight

          ScrollTrigger.create({
            trigger: textBlock,
            start: `${startOffset}px bottom`,
            end: `${endOffset}px bottom`,
            scrub: 1,
            onUpdate: (self) => {
              const progress = self.progress
              gsap.set(lineMask, { width: `${100 - progress * 100}%` })
            },
            onLeave: () => {
              // Keep line revealed when scrolled past
              gsap.set(lineMask, { width: "0%" })
            },
            onEnterBack: () => {
              // Reset when scrolling back
              gsap.set(lineMask, { width: "100%" })
            },
            onLeaveBack: () => {
              // Hide when scrolling back above
              gsap.set(lineMask, { width: "100%" })
            },
            markers: false, // Set to true for debugging
          })
        }
      })
    })

    ScrollTrigger.refresh()
  }

  // Initialize everything
  function init() {
    const scrollPosition = window.scrollY
    runSplit()
    createAnimation()
    window.scrollTo(0, scrollPosition)
  }

  // Wait for fonts to load
  async function waitForFonts() {
    if ("fonts" in document) {
      try {
        await document.fonts.ready
        return true
      } catch (error) {
        console.warn("Font loading failed:", error)
        return false
      }
    }
    return false
  }

  // Initialize after fonts are loaded
  async function initWithFonts() {
    await waitForFonts()

    // Add delay for Safari to ensure proper text measurement
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const delay = isSafari ? 500 : 100

    setTimeout(() => {
      init()
    }, delay)
  }

  // Ensure the text is split after fonts and page are loaded
  if (document.readyState === "loading") {
    window.addEventListener("load", initWithFonts)
  } else {
    initWithFonts()
  }

  // Update on window resize
  let windowWidth = window.innerWidth
  let resizeTimeout
  window.addEventListener("resize", function () {
    if (windowWidth !== window.innerWidth) {
      windowWidth = window.innerWidth
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(async () => {
        await waitForFonts()
        init()
      }, 300)
    }
  })

  // Handle dynamic content changes
  const container = document.querySelector("#portfolio")
  if (container) {
    const observer = new MutationObserver(async (mutationsList, observer) => {
      await waitForFonts()
      setTimeout(() => {
        init()
      }, 100)
      observer.disconnect()
    })

    const parksBtns = document.querySelectorAll(".parks__btn")
    parksBtns.forEach((parksBtn) => {
      parksBtn.addEventListener("click", (event) => {
        event.preventDefault()
        observer.observe(container, { childList: true, subtree: true })
        setTimeout(() => {
          window.dispatchEvent(new Event("resize"))
        }, 100)
      })
    })
  }
}
